import { useCallback, useMemo, useState } from 'react';
import { useResizeObserver } from '@tanium/react-use-resize-observer';

export type UseDimensionsReturnType<TElement = Element> = [
  /**
   * A reference to the element that may be resized.
   */
  ref: (node: TElement | null) => void,
  /**
   * The dimensions of the referenced element.
   */
  rect: {
    /**
     * The height of the referenced element's content rect.
     */
    height: number | undefined;
    /**
     * The width of the referenced element's content rect.
     */
    width: number | undefined;
    /**
     * The left boundary of the referenced element.
     */
    left: number | undefined;
    /**
     * The right boundary of the referenced element.
     */
    right: number | undefined;
  },
];

export interface UseDimensionsParams {
  /**
   * Whether to watch for changes to the height of the referenced element.
   * @default false
   */
  height?: boolean;
  /**
   * Whether to watch for changes to the width of the referenced element.
   * @default false
   */
  width?: boolean;
  /**
   * Whether to watch for changes to the left boundary of the referenced element.
   * @default false
   */
  left?: boolean;
  /**
   * Whether to watch for changes to the right boundary of the referenced element.
   * @default false
   */
  right?: boolean;
}

/**
 * Hook for determining the dimensions of an element.
 *
 * By default, no properties are watched for changes. You must specify the
 * properties you want to watch.
 */
export const useDimensions = <TElement extends Element>({
  height: watchHeightProp = false,
  width: watchWidthProp = false,
  left: watchLeftProp = false,
  right: watchRightProp = false,
}: UseDimensionsParams): UseDimensionsReturnType<TElement> => {
  // Ensure all props are booleans to avoid potential callback churn if props
  // change between, e.g., different truthy values
  const watchHeight = !!watchHeightProp;
  const watchWidth = !!watchWidthProp;
  const watchLeft = !!watchLeftProp;
  const watchRight = !!watchRightProp;

  const [width, setWidth] = useState<number>();
  const [height, setHeight] = useState<number>();
  const [left, setLeft] = useState<number>();
  const [right, setRight] = useState<number>();

  const ref = useResizeObserver<TElement>(
    useCallback(
      (e) => {
        if (watchWidth) {
          setWidth(e.contentRect.width);
        }

        if (watchHeight) {
          setHeight(e.contentRect.height);
        }

        if (watchLeft || watchRight) {
          const boundingRect = e.target.getBoundingClientRect();
          if (watchLeft) {
            setLeft(boundingRect.left);
          }
          if (watchRight) {
            setRight(boundingRect.right);
          }
        }
      },
      [watchHeight, watchLeft, watchRight, watchWidth],
    ),
  );

  const rect = useMemo(() => ({ width, height, left, right }), [height, left, right, width]);

  return [ref, rect];
};
